/*
Copyright 2024-2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  BackgroundTransformer,
  VideoTransformer,
  type TrackTransformerDestroyOptions,
  type VideoTransformerInitOptions,
} from "@livekit/track-processors";
import { ImageSegmenter } from "@mediapipe/tasks-vision";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  backgroundRenderingIsStale,
  coverDrawRect,
  deferBackgroundUntilNextFrame,
  whileCanvasSizeStays,
} from "./backgroundFrameSync";
import { backgroundImageUsesAnonymousCors } from "./callBackgroundImage";
import modelAssetPath from "../mediapipe/imageSegmenter/selfie_segmenter.tflite?url";

interface WasmFileset {
  /** The path to the Wasm loader script. */
  wasmLoaderPath: string;
  /** The path to the Wasm binary. */
  wasmBinaryPath: string;
}

// The MediaPipe package, by default, ships some alternative versions of the
// WASM files which avoid SIMD for compatibility with older browsers. But SIMD
// in WASM is actually fine by our support policy, so we include just the SIMD
// versions.
// It's really not ideal that we have to reference these internal files from
// MediaPipe and depend on node_modules having this specific structure. It's
// easy to see this breaking if our dependencies changed and MediaPipe were
// no longer hoisted, or if we switched to another dependency loader such as
// yarn PnP.
// https://github.com/google-ai-edge/mediapipe/issues/5961
const wasmFileset: WasmFileset = {
  wasmLoaderPath: new URL(
    "../../node_modules/@mediapipe/tasks-vision/wasm/vision_wasm_internal.js",
    import.meta.url,
  ).href,
  wasmBinaryPath: new URL(
    "../../node_modules/@mediapipe/tasks-vision/wasm/vision_wasm_internal.wasm",
    import.meta.url,
  ).href,
};

/**
 * Track processor that places a still image behind the person.
 *
 * This is just like LiveKit's prebuilt BackgroundTransformer except that it
 * loads the segmentation models from our own bundle rather than as an external
 * resource fetched from the public internet. Blur and the still image are
 * mutually exclusive: a blur radius would clear the image.
 */
export class BlurBackgroundTransformer extends BackgroundTransformer {
  /**
   * Kept for the whole call. LiveKit uploads the image once; after the camera
   * size changes or the GPU context is dropped that upload is gone.
   */
  private backgroundSource: ImageBitmap | null = null;

  /** Canvas size the current upload was made for. */
  private appliedWidth = 0;

  private appliedHeight = 0;

  /** Set when the GPU context is lost. The next real frame rebuilds once. */
  private renderingNeedsRebuild = false;

  /** A camera switch must not start a second rebuild while the first runs. */
  private rebuildInFlight = false;

  /**
   * The first rebuilds often run before the camera picture exists. Keep
   * trying, then stay on the real camera.
   */
  private static readonly maxRebuildAttempts = 8;

  private rebuildFailures = 0;

  private rebuildBlockedUntil = 0;

  /** Destroy won the race with a rebuild. The new segmenter must not be kept. */
  private destroyed = false;

  private contextLossCanvas: OffscreenCanvas | HTMLCanvasElement | undefined;

  private contextLossAbort: AbortController | undefined;

  public async init({
    outputCanvas,
    inputElement: inputVideo,
  }: VideoTransformerInitOptions): Promise<void> {
    // Call super.super.init() since we're totally replacing the init method of
    // BackgroundTransformer here, rather than extending it
    await VideoTransformer.prototype.init.call(this, {
      outputCanvas,
      inputElement: inputVideo,
    });

    this.destroyed = false;
    this.renderingNeedsRebuild = false;
    this.rebuildFailures = 0;
    this.appliedWidth = 0;
    this.appliedHeight = 0;
    await this.createSegmenter();
    this.bindContextLoss();

    if (
      typeof this.options.imagePath === "string" &&
      this.options.blurRadius === undefined
    ) {
      try {
        await this.loadStillBackground(this.options.imagePath);
      } catch (error) {
        // Keep the real camera. A failed image must not leave the processor
        // painting an empty background, and it must not fall back to blur.
        logger.warn("Failed to load the call background image", error);
        this.backgroundSource?.close();
        this.backgroundSource = null;
        this.options = { ...this.options, imagePath: undefined };
      }
    } else if (typeof this.options.blurRadius === "number") {
      this.gl?.setBlurRadius(this.options.blurRadius);
    }
  }

  public override async transform(
    frame: VideoFrame,
    controller: TransformStreamDefaultController<VideoFrame>,
  ): Promise<void> {
    const canvas = this.canvas;
    if (
      canvas !== undefined &&
      canvas !== null &&
      this.backgroundSource !== null &&
      backgroundRenderingIsStale(
        canvas.width,
        canvas.height,
        frame.displayWidth,
        frame.displayHeight,
        this.renderingNeedsRebuild,
      )
    ) {
      const restored = await this.restoreForFrame(
        frame.displayWidth,
        frame.displayHeight,
      );
      if (!restored) {
        this.passCameraThrough(frame, controller);
        return;
      }
    }
    if (this.canvas === undefined || this.canvas === null) {
      await super.transform(frame, controller);
      return;
    }
    // The upload from init is dropped when the first frame resizes the
    // canvas. Upload again, then draw this frame instead of waiting for the
    // hidden video element.
    if (this.backgroundSource !== null && this.isFirstFrame) {
      this.appliedWidth = 0;
      this.appliedHeight = 0;
    }
    this.isFirstFrame = deferBackgroundUntilNextFrame(
      this.backgroundSource !== null,
    );
    await this.uploadBackground();
    // LiveKit assigns the frame size on every frame. When it already matches,
    // WebKit still clears the context and the still image disappears.
    await whileCanvasSizeStays(this.canvas, async () => {
      await super.transform(frame, controller);
    });
  }

  public override async destroy(
    options?: TrackTransformerDestroyOptions,
  ): Promise<void> {
    this.destroyed = true;
    this.closeSegmenter();
    this.backgroundSource?.close();
    this.backgroundSource = null;
    this.appliedWidth = 0;
    this.appliedHeight = 0;
    this.renderingNeedsRebuild = false;
    this.contextLossAbort?.abort();
    await super.destroy(options);
  }

  /**
   * The widget file is root-relative and same-origin. LiveKit's loader always
   * sets crossOrigin anonymous, which this server rejects for its own files.
   */
  private async loadStillBackground(path: string): Promise<void> {
    const img = new Image();
    if (backgroundImageUsesAnonymousCors(path)) {
      img.crossOrigin = "anonymous";
    }
    await new Promise<void>((resolve, reject) => {
      img.onload = (): void => resolve();
      img.onerror = (): void => reject(new Error("background image failed"));
      img.src = path;
    });
    this.backgroundSource?.close();
    this.backgroundSource = await createImageBitmap(img);
    await this.uploadBackground();
  }

  /**
   * The display size is the rotated camera picture. Rebuilding on every frame
   * crashed the iOS GPU process; one rebuild per size change turns the
   * picture upright and puts the still image back.
   */
  private async restoreForFrame(
    width: number,
    height: number,
  ): Promise<boolean> {
    if (this.rebuildInFlight || this.destroyed) return false;
    if (width <= 0 || height <= 0) return false;
    const now = performance.now();
    if (
      this.rebuildFailures >= BlurBackgroundTransformer.maxRebuildAttempts ||
      now < this.rebuildBlockedUntil
    ) {
      return false;
    }
    const inputVideo = this.inputVideo;
    if (inputVideo === undefined) return false;

    this.rebuildInFlight = true;
    try {
      this.closeSegmenter();
      const canvas = createProcessingCanvas(width, height);
      await super.restart({ outputCanvas: canvas, inputElement: inputVideo });
      await this.createSegmenter();
      this.bindContextLoss();
      this.appliedWidth = 0;
      this.appliedHeight = 0;
      this.renderingNeedsRebuild = false;
      this.rebuildFailures = 0;
      await this.uploadBackground();
      return !this.destroyed;
    } catch (error) {
      this.rebuildFailures += 1;
      this.rebuildBlockedUntil = performance.now() + 1000;
      this.renderingNeedsRebuild = true;
      logger.warn("Failed to restore the call background", error);
      return false;
    } finally {
      this.rebuildInFlight = false;
    }
  }

  /**
   * Hands the untouched camera frame on when the drawing surface cannot be
   * rebuilt. The call keeps the real camera.
   */
  private passCameraThrough(
    frame: VideoFrame,
    controller: TransformStreamDefaultController<VideoFrame>,
  ): void {
    try {
      controller.enqueue(frame);
    } catch (error) {
      frame.close();
      logger.warn("Failed to pass the camera frame through", error);
    }
  }

  private closeSegmenter(): void {
    const segmenter = this.imageSegmenter;
    this.imageSegmenter = undefined;
    try {
      segmenter?.close();
    } catch (error) {
      logger.warn("Failed to close the background segmenter", error);
    }
  }

  private async createSegmenter(): Promise<void> {
    const segmenter = await ImageSegmenter.createFromOptions(wasmFileset, {
      baseOptions: {
        modelAssetPath,
        delegate: "GPU",
        ...this.options.segmenterOptions,
      },
      canvas: this.canvas,
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });
    if (this.destroyed) {
      segmenter.close();
      return;
    }
    this.imageSegmenter = segmenter;
  }

  private async uploadBackground(): Promise<void> {
    const bitmap = this.backgroundSource;
    const canvas = this.canvas;
    if (bitmap === null || canvas === undefined || canvas === null) return;
    if (canvas.width <= 0 || canvas.height <= 0) return;
    if (
      this.appliedWidth === canvas.width &&
      this.appliedHeight === canvas.height
    ) {
      return;
    }
    let covered = bitmap;
    try {
      covered = await this.coverBackground(bitmap, canvas.width, canvas.height);
    } catch (error) {
      logger.warn("Failed to crop the call background", error);
      covered = bitmap;
    }
    try {
      await this.gl?.setBackgroundImage(covered);
    } finally {
      if (covered !== bitmap) covered.close();
    }
    this.appliedWidth = canvas.width;
    this.appliedHeight = canvas.height;
  }

  /**
   * LiveKit scales the whole image to the canvas. A wide picture then looks
   * squeezed on a tall camera. Cut it to the camera shape first.
   */
  private async coverBackground(
    bitmap: ImageBitmap,
    width: number,
    height: number,
  ): Promise<ImageBitmap> {
    if (bitmap.width === width && bitmap.height === height) return bitmap;
    const canvas = createProcessingCanvas(width, height);
    const context = canvas.getContext("2d");
    if (context === null) return bitmap;
    const rect = coverDrawRect(bitmap.width, bitmap.height, width, height);
    context.drawImage(bitmap, rect.dx, rect.dy, rect.dw, rect.dh);
    return createImageBitmap(canvas);
  }

  private bindContextLoss(): void {
    const canvas = this.canvas;
    if (canvas === undefined || canvas === null) return;
    if (this.contextLossCanvas === canvas) return;
    this.contextLossAbort?.abort();
    this.contextLossAbort = new AbortController();
    const { signal } = this.contextLossAbort;
    this.contextLossCanvas = canvas;
    canvas.addEventListener(
      "webglcontextlost",
      (event: Event) => {
        event.preventDefault();
        this.renderingNeedsRebuild = true;
      },
      { signal },
    );
    canvas.addEventListener(
      "webglcontextrestored",
      () => {
        this.renderingNeedsRebuild = true;
      },
      { signal },
    );
  }
}

function createProcessingCanvas(
  width: number,
  height: number,
): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
